import { isPlatformBrowser } from '@angular/common';

import { AppComponent } from 'app/app.component';
import { getUserInitials } from 'app/core/utilities/user-util';
import SocialTheme from 'entity/SocialTheme';
import User from 'entity/User';

import { FONT_FAMILY } from '../constants';
import { strToHex } from '../utils/theme';
import { Container, Sprite, Application, Graphics, Text, TextStyle } from 'pixi.js';

export default class MiniMapParticipant extends Container {
  private dot?: Sprite;
  private initialsLabel: Text;

  public constructor(
    private app: Application,
    public radius: number,
    user: User,
    private theme: SocialTheme | undefined,
    private isMainParticipant: boolean,
    private newVisualIndicator: boolean,
    private ringColor?: string,
  ) {
    super();
    this.sortableChildren = true;
    this.eventMode = 'static';
    this.setRadius(radius);

    this.zIndex = isMainParticipant ? 10 : 0;

    // Initials Text
    const thisUserInitials = getUserInitials(user);

    if (this.newVisualIndicator) {
      this.initialsLabel = new Text(
        thisUserInitials,
        new TextStyle({
          fontFamily: FONT_FAMILY,
          fontSize: 12,
          fill: this.isMainParticipant ? '#000' : '#fff',
          align: 'center',
        }),
      );
    } else {
      this.initialsLabel = new Text(
        thisUserInitials,
        new TextStyle({
          fontFamily: FONT_FAMILY,
          fontSize: 12,
          fill: this.color,
          align: 'center',
        }),
      );
      this.initialsLabel.name = 'initials';
    }

    this.initialsLabel.anchor.set(0.5, 0.5);

    this.addChild(this.initialsLabel);
    this.setInitialsVisibility();
  }

  // deprecated with the new visual indicator
  public get color(): number {
    return strToHex(this.theme?.colorPrimary ?? `#777`);
  }

  // deprecated with the new visual indicator
  // Used to highlight a participant
  public set color(value: number) {
    this.children.forEach((c) => {
      if (c.name === 'initials') {
        const s = c as Text;
        s.style.fill = 0xffffff;
      } else if (c.name === 'dot') {
        const g = c as Graphics;
        g.clear();
        g.beginFill(value, 1);
        g.lineStyle(1, 0xffffff);
        g.drawCircle(0, 0, this.radius);
        g.endFill();
      }
    });
  }

  public setRadius(radius: number): void {
    let newRadius = radius;
    if (radius < 4) {
      newRadius = 5;
    }
    const diameter = newRadius * 2;
    this.radius = newRadius;
    this.width = diameter;
    this.height = diameter;
    this.setInitialsVisibility();
    this.setDotGraphic();
  }

  // TODO: reference a central template.geometry
  public setDotGraphic(ringColor = this.ringColor): void {
    this.ringColor = ringColor;
    if (this.dot) {
      this.destroyDot();
    }
    if (!isPlatformBrowser(AppComponent.PLATFORM_ID)) {
      return;
    }

    let fillColor;
    let lineColor;
    const ratio = 2;

    if (this.newVisualIndicator) {
      fillColor = this.ringColor ? strToHex(this.ringColor) : this.isMainParticipant ? 0xffffff : 0xadadac;
      lineColor = this.isMainParticipant ? strToHex('#000000') : strToHex('#ffffff');
    } else {
      fillColor = this.isMainParticipant ? 0xffffff : 0xadadad;
      lineColor = this.color;
    }

    const graphics = new Graphics();
    graphics.beginFill(fillColor, 1);
    graphics.lineStyle(2 * ratio, lineColor, 1);
    graphics.drawCircle(0, 0, this.radius * ratio);
    graphics.endFill();

    const texture = this.app.renderer.generateTexture(graphics);
    this.dot = new Sprite(texture);
    this.dot.anchor.set(0.5, 0.5);
    this.dot.scale.set(1 / ratio);
    this.dot.name = 'dot';
    this.addChildAt(this.dot, 0);
  }

  private destroyDot(): void {
    this.dot?.destroy();
    this.dot = undefined;
  }

  private setInitialsVisibility(): void {
    if (!this.initialsLabel) {
      return;
    }
    this.initialsLabel.visible = this.radius <= 10 ? false : true;
  }

  public setTheme(theme: SocialTheme) {
    this.theme = theme;
    this.initialsLabel.style.fill = this.color;
    this.setDotGraphic();
  }
}
